/**
 * `sim_end_time` on the wire is an ISO 8601 instant with a timezone — that is
 * what the simulator validates and what `simHasEnded` compares against. The
 * card edits it with `<input type="datetime-local">`, and that input has NO
 * timezone: it shows and returns a bare wall-clock reading in the operator's
 * own zone.
 *
 * So the two directions are not symmetrical, and getting one of them wrong is
 * invisible until somebody reads the clock:
 *
 *   - putting the UTC string straight into the field labels 22:00Z as "22:00",
 *     which an operator in Texas reads as 22:00 THEIR time — six hours late;
 *   - and re-picking that displayed value writes 22:00 local back out as
 *     04:00Z the next day, so the value walks forward by the UTC offset every
 *     time the field is touched.
 *
 * These two functions are the conversion, kept apart from the component so the
 * round trip can be tested rather than eyeballed.
 */

/** The wall-clock text `datetime-local` wants, for an instant on the wire. */
export const toLocalInputValue = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  // Shift by the zone offset so toISOString() prints LOCAL wall-clock digits.
  const localMs = at.getTime() - at.getTimezoneOffset() * 60_000;
  return new Date(localMs).toISOString().slice(0, 16);
};

/** The instant for a wall-clock reading the operator typed. Null when cleared. */
export const fromLocalInputValue = (value: string | null | undefined): string | null => {
  if (!value) return null;
  // `new Date('2026-09-01T16:00')` is parsed in the browser's own zone, which
  // is exactly what the field meant.
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return at.toISOString();
};

/** Now, as the field's `min` — a datetime-local min must carry a time too. */
export const nowLocalInputValue = (): string => toLocalInputValue(new Date().toISOString());
